import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { InstallBanner } from "../components/InstallPrompt";

const InstallContext = createContext(null);
export const useInstall = () => useContext(InstallContext);

const DISMISS_KEY = "aca_pwa_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

export function InstallProvider({ children }) {
  const deferred = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ios] = useState(isIOS());
  const [installed, setInstalled] = useState(isStandalone());
  const [hasNative, setHasNative] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferred.current = e;
      setHasNative(true);
      if (localStorage.getItem(DISMISS_KEY) !== "1") setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS ne déclenche pas beforeinstallprompt : proposer à la première visite.
    let iosTimer;
    if (isIOS() && localStorage.getItem(DISMISS_KEY) !== "1") {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  // Ré-ouvre l'invitation à la demande (bouton menu), même après un refus.
  const open = useCallback(() => setVisible(true), []);

  const close = useCallback(() => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  }, []);

  const promptNative = useCallback(async () => {
    const evt = deferred.current;
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {
      /* ignore */
    }
    deferred.current = null;
    setHasNative(false);
    close();
  }, [close]);

  return (
    <InstallContext.Provider value={{ open, canInstall: !installed }}>
      {children}
      {visible && !installed && (
        <InstallBanner ios={ios} hasNative={hasNative} onInstall={promptNative} onClose={close} />
      )}
    </InstallContext.Provider>
  );
}
